import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Archive,
  ArchiveRestore,
  ArrowUpDown,
  Gauge,
  Pencil,
  PlugZap,
  Plus,
  Server,
  Timer,
  TrendingDown,
  Weight,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { AutocompleteSelect, Button, EmptyState, Skeleton, Toggle } from '../ui';
import { useMutation, useQuery } from '../gql/hooks';
import { useSubscription } from '../gql/useSubscription';
import {
  ARCHIVE_PROVIDER_MUTATION,
  PROBE_PROVIDER_MUTATION,
  PROVIDER_HEALTH_CHANGED_SUBSCRIPTION,
  PROVIDERS_QUERY,
  SET_PROVIDER_ENABLED_MUTATION,
  SYSTEM_HEARTBEAT_SUBSCRIPTION,
  UNARCHIVE_PROVIDER_MUTATION,
  UPSERT_PROVIDER_MUTATION,
  type ArchiveProviderResult,
  type ArchiveProviderVariables,
  type ProbeProviderMutationResult,
  type ProbeProviderMutationVariables,
  type ProviderHealthChangedSubscriptionResult,
  type ProvidersQueryResult,
  type ProvidersQueryVariables,
  type SystemHeartbeatSubscriptionResult,
  type ProviderProbeResult,
  type SetProviderEnabledMutationResult,
  type SetProviderEnabledMutationVariables,
  type UnarchiveProviderResult,
  type UpsertProviderMutationResult,
  type UpsertProviderMutationVariables,
} from '../gql/operations';
import { HealthState, ProviderType, type Provider, type UpsertProviderInput } from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { LiveConnectionIndicator } from '../features/providers/LiveConnectionIndicator';
import { HealthStateBadge } from '../features/shared/StatusBadge';
import { InlineBanner } from '../features/shared/InlineBanner';
import { providerLabel } from '../features/shared/providerLabels';
import { ProbeResultBadge } from '../features/providers/ProbeResultBadge';
import { ProviderFormModal } from '../features/providers/ProviderFormModal';
import { formatRelativeCompact } from '../lib/format';

// Provider health board (US-8.2.1 + live, US-8.6.2). A grid of provider cards showing health
// (state, in-flight, consecutive failures, last probe) plus routing controls (enabled toggle,
// weight, max concurrency), an Add/Edit provider form, and a per-card "test connection" probe.
//
// LIVE-UPDATE SEAM (C29): the `providerHealthChanged` subscription now feeds this board. We lift
// the `providers` query result into local state keyed by id, and the subscription handler upserts
// the changed Provider into that SAME list — so the board updates live with NO card/markup change.

const HEALTH_ACCENT: Record<HealthState, string> = {
  [HealthState.Healthy]: 'from-emerald-500 to-teal-600',
  [HealthState.Degraded]: 'from-amber-500 to-orange-600',
  [HealthState.Down]: 'from-rose-500 to-red-600',
};

function Metric({
  icon,
  label,
  value,
  emphasis = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Flash the value briefly whenever it changes so a 200 ms transient (in_flight bouncing
  // 0 → 1 → 0 during a fast upstream call) is still visible to a human eye. We compare the
  // previous value to the current; when they differ we toggle a one-off `flash` class for
  // ~600 ms via a setTimeout so React doesn't drop the indicator on rapid bounces. Without
  // this the value would update in the DOM but be unnoticeable on a still board.
  const [flash, setFlash] = useState(false);
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (prevValueRef.current === value) return;
    prevValueRef.current = value;
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 600);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="flex items-center gap-2">
      <span className={isDark ? 'text-white/30' : 'text-slate-400'} aria-hidden="true">
        {icon}
      </span>
      <span className="flex flex-col">
        <span
          className={`text-[10px] font-medium uppercase tracking-widest ${
            isDark ? 'text-white/30' : 'text-slate-400'
          }`}
        >
          {label}
        </span>
        <span
          data-flashing={flash ? 'true' : 'false'}
          className={`tabular-nums text-sm font-semibold transition-colors duration-500 ${
            flash
              ? isDark
                ? 'text-indigo-200 [text-shadow:_0_0_8px_rgba(165,180,252,0.6)]'
                : 'text-indigo-600 [text-shadow:_0_0_8px_rgba(99,102,241,0.4)]'
              : emphasis
                ? isDark
                  ? 'text-rose-300'
                  : 'text-rose-600'
                : isDark
                  ? 'text-white/80'
                  : 'text-slate-700'
          }`}
        >
          {value}
        </span>
      </span>
    </div>
  );
}

function ProviderCard({
  provider,
  onToggle,
  onEdit,
  onProbe,
  onArchive,
  toggling,
  probing,
  archiving,
  probeResult,
}: {
  provider: Provider;
  onToggle: (provider: Provider, next: boolean) => void;
  onEdit: (provider: Provider) => void;
  onProbe: (provider: Provider) => void;
  onArchive: (provider: Provider) => void;
  toggling: boolean;
  probing: boolean;
  archiving: boolean;
  probeResult: ProviderProbeResult | null;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { health } = provider;
  const accent = HEALTH_ACCENT[health.state];

  return (
    <article
      aria-label={provider.displayName}
      className={`group relative overflow-hidden rounded-2xl border p-5 transition-all duration-300 ${
        provider.enabled ? '' : 'opacity-60'
      } ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02] hover:border-white/[0.12]'
          : 'border-slate-200 bg-white shadow-sm hover:shadow-md'
      }`}
    >
      {/* Health-tinted ambient corner — depth + at-a-glance state (§10). */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br ${accent} opacity-[0.12] blur-2xl transition-opacity duration-500 group-hover:opacity-25`}
      />

      <header className="relative flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span
            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-lg`}
          >
            <Server className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h3
              className={`truncate text-base font-semibold ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              {provider.displayName}
            </h3>
            <p
              title={`${providerLabel(provider.type)} · ${provider.baseUrl}`}
              className={`truncate font-mono text-[11px] ${
                isDark ? 'text-white/30' : 'text-slate-400'
              }`}
            >
              {providerLabel(provider.type)} · {provider.baseUrl}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <HealthStateBadge state={health.state} />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onEdit(provider)}
            leftIcon={<Pencil className="h-4 w-4" aria-hidden="true" />}
            aria-label={`Edit ${provider.displayName}`}
          >
            Edit
          </Button>
        </div>
      </header>

      <dl className="relative mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Metric
          icon={<Activity className="h-4 w-4" />}
          label="In flight"
          value={String(health.inFlight)}
        />
        <Metric
          icon={<TrendingDown className="h-4 w-4" />}
          label="Failures"
          value={String(health.consecutiveFailures)}
          emphasis={health.consecutiveFailures > 0}
        />
        <Metric
          icon={<Timer className="h-4 w-4" />}
          label="Last probe"
          value={health.lastProbeAt ? formatRelativeCompact(health.lastProbeAt) : 'never'}
        />
        <Metric
          icon={<Weight className="h-4 w-4" />}
          label="Weight"
          value={String(provider.weight)}
        />
        <Metric
          icon={<Gauge className="h-4 w-4" />}
          label="Max conc."
          value={String(provider.maxConcurrency)}
        />
        {provider.dataResidency && (
          <Metric
            icon={<Server className="h-4 w-4" />}
            label="Residency"
            value={provider.dataResidency.toUpperCase()}
          />
        )}
      </dl>

      {/* 2026-06-08 — rolling 24h metrics from `usage_records`. Hidden when the provider
          hasn't served any traffic in the window (provider.metrics24h === null) so the
          card stays compact for new/idle providers. */}
      {provider.metrics24h && (
        <dl
          className={`relative mt-4 grid grid-cols-3 gap-3 rounded-lg border px-3 py-2 ${
            isDark
              ? 'border-white/[0.06] bg-white/[0.02]'
              : 'border-slate-200 bg-slate-50'
          }`}
        >
          <div>
            <dt
              className={`text-[10px] uppercase tracking-wide ${
                isDark ? 'text-white/40' : 'text-slate-500'
              }`}
            >
              24h reqs
            </dt>
            <dd
              className={`text-sm font-semibold ${
                isDark ? 'text-white/85' : 'text-slate-900'
              }`}
            >
              {provider.metrics24h.requestCount}
            </dd>
          </div>
          <div>
            <dt
              className={`text-[10px] uppercase tracking-wide ${
                isDark ? 'text-white/40' : 'text-slate-500'
              }`}
            >
              Err rate
            </dt>
            <dd
              className={`text-sm font-semibold ${
                (provider.metrics24h.errorRate ?? 0) > 0.05
                  ? isDark
                    ? 'text-rose-300'
                    : 'text-rose-600'
                  : isDark
                    ? 'text-white/85'
                    : 'text-slate-900'
              }`}
            >
              {provider.metrics24h.errorRate == null
                ? '—'
                : `${(provider.metrics24h.errorRate * 100).toFixed(1)}%`}
            </dd>
          </div>
          <div>
            <dt
              className={`text-[10px] uppercase tracking-wide ${
                isDark ? 'text-white/40' : 'text-slate-500'
              }`}
            >
              Avg lat
            </dt>
            <dd
              className={`text-sm font-semibold ${
                isDark ? 'text-white/85' : 'text-slate-900'
              }`}
            >
              {provider.metrics24h.avgLatencyMs == null
                ? '—'
                : `${Math.round(provider.metrics24h.avgLatencyMs)}ms`}
            </dd>
          </div>
        </dl>
      )}

      {/* Test connection — actively probe the upstream; result shows inline below the button. */}
      <div
        className={`relative mt-5 flex flex-wrap items-center gap-3 border-t pt-4 ${
          isDark ? 'border-white/[0.06]' : 'border-slate-100'
        }`}
      >
        <Button
          variant="secondary"
          size="sm"
          loading={probing}
          onClick={() => onProbe(provider)}
          leftIcon={<PlugZap className="h-4 w-4" aria-hidden="true" />}
          aria-label={`Test connection to ${provider.displayName}`}
        >
          Test connection
        </Button>
        {/* 2026-06-08: archive / restore toggle. Archived providers are hidden by the
            default board view and skipped by the routing engine, but their data is kept. */}
        <Button
          variant="secondary"
          size="sm"
          loading={archiving}
          onClick={() => onArchive(provider)}
          leftIcon={
            provider.archivedAt ? (
              <ArchiveRestore className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Archive className="h-4 w-4" aria-hidden="true" />
            )
          }
          aria-label={
            provider.archivedAt
              ? `Restore ${provider.displayName}`
              : `Archive ${provider.displayName}`
          }
        >
          {provider.archivedAt ? 'Restore' : 'Archive'}
        </Button>
        {probeResult && <ProbeResultBadge result={probeResult} />}
      </div>

      <footer
        className={`relative mt-4 flex items-center justify-between border-t pt-4 ${
          isDark ? 'border-white/[0.06]' : 'border-slate-100'
        }`}
      >
        <span
          className={`text-xs font-medium ${
            provider.enabled
              ? isDark
                ? 'text-emerald-300'
                : 'text-emerald-600'
              : isDark
                ? 'text-white/40'
                : 'text-slate-400'
          }`}
        >
          {provider.enabled ? 'Routing enabled' : 'Excluded from routing'}
        </span>
        <Toggle
          checked={provider.enabled}
          loading={toggling}
          onChange={(next) => onToggle(provider, next)}
          label={`${provider.enabled ? 'Disable' : 'Enable'} ${provider.displayName}`}
        />
      </footer>
    </article>
  );
}

type SortKey =
  | 'newest'
  | 'name'
  | 'type'
  | 'inFlight'
  | 'requests24h'
  | 'errorRate24h'
  | 'avgLatency24h';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'name', label: 'Name (A→Z)' },
  { value: 'type', label: 'Provider type' },
  { value: 'inFlight', label: 'In-flight (high→low)' },
  { value: 'requests24h', label: 'Requests (24h, high→low)' },
  { value: 'errorRate24h', label: 'Error rate (24h, high→low)' },
  { value: 'avgLatency24h', label: 'Avg latency (24h, high→low)' },
];

function sortProviders(providers: Provider[], key: SortKey): Provider[] {
  const copy = [...providers];
  switch (key) {
    case 'newest':
      // Server already returns newest first; preserve incoming order.
      return copy;
    case 'name':
      return copy.sort((a, b) => a.displayName.localeCompare(b.displayName));
    case 'type':
      return copy.sort(
        (a, b) => a.type.localeCompare(b.type) || a.displayName.localeCompare(b.displayName),
      );
    case 'inFlight':
      return copy.sort((a, b) => b.health.inFlight - a.health.inFlight);
    case 'requests24h':
      return copy.sort(
        (a, b) => (b.metrics24h?.requestCount ?? 0) - (a.metrics24h?.requestCount ?? 0),
      );
    case 'errorRate24h':
      return copy.sort(
        (a, b) => (b.metrics24h?.errorRate ?? 0) - (a.metrics24h?.errorRate ?? 0),
      );
    case 'avgLatency24h':
      return copy.sort(
        (a, b) => (b.metrics24h?.avgLatencyMs ?? 0) - (a.metrics24h?.avgLatencyMs ?? 0),
      );
    default:
      return copy;
  }
}

export function ProviderHealthBoardPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [showArchived, setShowArchived] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const { data, loading, error, refetch } = useQuery<
    ProvidersQueryResult,
    ProvidersQueryVariables
  >(PROVIDERS_QUERY, { includeArchived: showArchived });
  const setEnabled = useMutation<
    SetProviderEnabledMutationResult,
    SetProviderEnabledMutationVariables
  >(SET_PROVIDER_ENABLED_MUTATION);
  const upsert = useMutation<
    UpsertProviderMutationResult,
    UpsertProviderMutationVariables
  >(UPSERT_PROVIDER_MUTATION);
  const archive = useMutation<ArchiveProviderResult, ArchiveProviderVariables>(
    ARCHIVE_PROVIDER_MUTATION,
  );
  const unarchive = useMutation<UnarchiveProviderResult, ArchiveProviderVariables>(
    UNARCHIVE_PROVIDER_MUTATION,
  );

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Provider | null>(null);

  // Per-card "test connection" probe state: which card is probing, each card's last result, and a
  // page-level probe error banner. A board mutation is used directly so results can be keyed by
  // provider id (the shared useProbeProvider hook holds a single result and powers the form modal).
  const probeMutation = useMutation<
    ProbeProviderMutationResult,
    ProbeProviderMutationVariables
  >(PROBE_PROVIDER_MUTATION);
  const [probingId, setProbingId] = useState<string | null>(null);
  const [probeResults, setProbeResults] = useState<Record<string, ProviderProbeResult>>({});
  const [probeError, setProbeError] = useState<string | null>(null);

  // The board's render source: the query result lifted into local state so live subscription
  // pushes can upsert into the SAME list (see LIVE-UPDATE SEAM above). Seeded/re-seeded whenever
  // the query (re)resolves; preserves order, replacing changed providers in place.
  const [providers, setProviders] = useState<Provider[]>([]);
  useEffect(() => {
    if (data?.providers) setProviders(data.providers);
  }, [data]);

  // `providerHealthChanged` — upsert the changed Provider by id, in place (no reorder, no markup
  // change). A push for an unknown provider is appended so a newly-registered provider appears.
  const handleHealthChanged = useCallback((payload: ProviderHealthChangedSubscriptionResult) => {
    const changed = payload.providerHealthChanged;
    if (!changed) return;
    setProviders((prev) => {
      const idx = prev.findIndex((p) => p.id === changed.id);
      if (idx === -1) return [...prev, changed];
      const next = [...prev];
      next[idx] = changed;
      return next;
    });
  }, []);

  // Capture the live-subscription status so the page header can show the operator a
  // green/amber/rose "Live" / "Reconnecting" / "Offline" pill. Also subscribes to the
  // server's 5s heartbeat so the same pill flips to "Stale" when no beat arrives within
  // 12s — catches the case where the socket reports healthy but delivery has silently
  // stalled (e.g. bridge crashed + restarted, proxy idle-killed the connection upstream).
  const { status: liveStatus, reconnect } =
    useSubscription<ProviderHealthChangedSubscriptionResult>(
      PROVIDER_HEALTH_CHANGED_SUBSCRIPTION,
      { onData: handleHealthChanged },
    );

  const [lastBeatAt, setLastBeatAt] = useState<Date | null>(null);
  const handleHeartbeat = useCallback((payload: SystemHeartbeatSubscriptionResult) => {
    if (payload?.systemHeartbeat?.at) setLastBeatAt(new Date(payload.systemHeartbeat.at));
  }, []);
  useSubscription<SystemHeartbeatSubscriptionResult>(SYSTEM_HEARTBEAT_SUBSCRIPTION, {
    onData: handleHeartbeat,
  });

  // Reset the heartbeat timer on any socket transition that implies a fresh start. The
  // useSubscription hook itself manages reconnection; here we just clear the staleness
  // signal so the operator sees "Connecting…" instead of "Stale (12s)" mid-cycle.
  useEffect(() => {
    if (liveStatus !== 'open') setLastBeatAt(null);
  }, [liveStatus]);

  const handleToggle = async (provider: Provider, next: boolean) => {
    setTogglingId(provider.id);
    try {
      await setEnabled.mutate({ id: provider.id, enabled: next });
      // Refetch so the board reflects the authoritative post-toggle state.
      refetch();
    } catch {
      // Error is recorded on setEnabled.error and rendered in the banner below.
    } finally {
      setTogglingId(null);
    }
  };

  const handleArchive = async (provider: Provider) => {
    const isCurrentlyArchived = provider.archivedAt != null;
    const ok = window.confirm(
      isCurrentlyArchived
        ? `Restore ${provider.displayName}? It will be visible again and eligible for routing if enabled.`
        : `Archive ${provider.displayName}? It will be hidden from the board and skipped by the router. All its data (model aliases, usage records) is preserved and can be restored later.`,
    );
    if (!ok) return;

    setArchivingId(provider.id);
    try {
      if (isCurrentlyArchived) {
        await unarchive.mutate({ id: provider.id });
      } else {
        await archive.mutate({ id: provider.id });
      }
      refetch();
    } catch {
      // Error is recorded on archive.error / unarchive.error.
    } finally {
      setArchivingId(null);
    }
  };

  const handleProbe = async (provider: Provider) => {
    setProbingId(provider.id);
    setProbeError(null);
    try {
      const result = await probeMutation.mutate({ id: provider.id });
      setProbeResults((prev) => ({ ...prev, [provider.id]: result.probeProvider }));
    } catch (cause: unknown) {
      setProbeError(
        cause instanceof Error ? cause.message : 'The connection test could not be run.',
      );
    } finally {
      setProbingId(null);
    }
  };

  // Types already configured (with duplicates preserved so the form can show a count).
  // 2026-06-08: multiple providers per type are now allowed — the form annotates the
  // type list with a "X configured" hint but no longer disables anything.
  const takenTypes = useMemo<ProviderType[]>(
    () => providers.map((p) => p.type),
    [providers],
  );

  const openCreate = () => {
    setEditing(null);
    upsert.reset();
    setFormOpen(true);
  };
  const openEdit = (provider: Provider) => {
    setEditing(provider);
    upsert.reset();
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    upsert.reset();
  };

  const handleUpsert = async (input: UpsertProviderInput) => {
    try {
      await upsert.mutate({ input });
      setFormOpen(false);
      setEditing(null);
      upsert.reset();
      refetch();
    } catch {
      // Error surfaced inline in the modal via upsert.error; keep the form open to retry.
    }
  };

  return (
    <div>
      <PageHeader
        title="Provider health"
        description="Upstream providers and their current routing health. Disable a provider to take it out of rotation."
        breadcrumbs={[{ label: 'Providers' }]}
        actions={
          <>
            {/* 2026-06-08: the LIVE/OFFLINE pill is also the page Refresh — clicking it
                forces a connection cycle AND a data refetch. Tiny refresh icon docked on
                the right so it stays the same size as before. The standalone Refresh
                button is gone. */}
            <LiveConnectionIndicator
              status={liveStatus}
              lastBeatAt={lastBeatAt}
              onReconnect={reconnect}
              onRefresh={refetch}
              refreshing={loading}
            />
            <Button
              variant="primary"
              onClick={openCreate}
              leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
            >
              Add provider
            </Button>
          </>
        }
      />

      {/* 2026-06-08: sort + show-archived controls. Uses AutocompleteSelect so the
          sort picker matches every other dropdown in the app (no native browser chrome).
          Sort runs client-side over the loaded providers; toggling showArchived
          re-issues the query with the new arg. */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <ArrowUpDown
            className={`h-3.5 w-3.5 ${isDark ? 'text-white/60' : 'text-slate-600'}`}
            aria-hidden="true"
          />
          <span
            className={`text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}
          >
            Sort by
          </span>
          <div className="min-w-[200px]">
            <AutocompleteSelect
              label="Sort providers"
              value={sortKey}
              options={SORT_OPTIONS}
              onChange={(next) => setSortKey(next as SortKey)}
            />
          </div>
        </div>
        <label
          className={`inline-flex cursor-pointer items-center gap-2 text-xs ${
            isDark ? 'text-white/60' : 'text-slate-600'
          }`}
        >
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <span>Show archived</span>
        </label>
      </div>

      {error && (
        <InlineBanner tone="error">Couldn’t load providers: {error.message}</InlineBanner>
      )}

      {setEnabled.error && (
        <InlineBanner tone="error" testId="mutation-error">
          That action couldn’t be completed: {setEnabled.error.message}
        </InlineBanner>
      )}

      {probeError && (
        <InlineBanner tone="error" testId="probe-error">
          The connection test could not be run: {probeError}
        </InlineBanner>
      )}

      {loading && providers.length === 0 && (
        <div
          className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
          data-testid="providers-loading"
        >
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="block" height={220} />
          ))}
        </div>
      )}

      {!loading && !error && providers.length === 0 && (
        <EmptyState
          icon={<Server className="h-16 w-16" aria-hidden="true" />}
          title="No providers configured"
          description="Upstream providers will appear here with their live health and routing controls."
          action={{ label: 'Add first provider', onClick: openCreate }}
        />
      )}

      {providers.length > 0 && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {sortProviders(providers, sortKey).map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              toggling={togglingId === provider.id}
              archiving={archivingId === provider.id}
              onToggle={(p, next) => void handleToggle(p, next)}
              onArchive={(p) => void handleArchive(p)}
              onEdit={openEdit}
              onProbe={(p) => void handleProbe(p)}
              probing={probingId === provider.id}
              probeResult={probeResults[provider.id] ?? null}
            />
          ))}
        </div>
      )}

      <ProviderFormModal
        isOpen={formOpen}
        onClose={closeForm}
        provider={editing}
        takenTypes={takenTypes}
        loading={upsert.loading}
        errorMessage={upsert.error?.message}
        onSubmit={(input) => void handleUpsert(input)}
        onAliasesChanged={refetch}
        onSilentSave={async (input) => {
          // Save without closing the modal so the operator can continue with their action
          // (Test connection / Reset & probe) against the just-persisted values. Refetches
          // so the modal's `provider` prop is the freshly-saved row.
          const result = await upsert.mutate({ input });
          refetch();
          return result.upsertProvider;
        }}
      />
    </div>
  );
}
